import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import axios from 'axios';
import ArticleModal from './ArticleModal';

const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US'); // MM/DD/YYYY
};

const ArticleArchive = () => {
  const [articles, setArticles] = useState([]);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  const articleColumns = [
    { name: 'Title', selector: row => row.title, sortable: true },
    { 
      name: 'Date',
      selector: row => new Date(row.date).getTime(), // for proper chronological sorting
      sortable: true,
      cell: row => formatDate(row.date) // displayed as MM/DD/YYYY
    },
    {
      name: 'Actions',
      cell: row => (
        <div className="admin-actions-container d-flex gap-2">
          <button 
            className="btn btn-outline-primary btn-sm" 
            onClick={() => handleViewArticle(row)}
            title="View Article Details"
          >
            <i className="fas fa-eye"></i>
          </button>
          <a 
            href={`/article/${row.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline-success btn-sm"
            title="View Full Article Page"
          >
            <i className="fas fa-file-text"></i>
          </a>
          {row.url && (
            <a 
              href={row.url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-info btn-sm"
              title="View Source Article"
            >
              <i className="fas fa-external-link-alt"></i>
            </a>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '180px'
    },
  ];

  const fetchArticles = async (page, perPage, search) => {
    setLoading(true);
    try {
      const params = {
        draw: page,
        start: (page - 1) * perPage,
        length: perPage,
        'search[value]': search,
      };
      const res = await axios.get('/api/articles', { params });
      setArticles(res.data.data);
      setTotalRows(res.data.recordsTotal);
    } catch (err) {
      setError('Failed to fetch articles.');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchArticles(page, perPage, search);
    // eslint-disable-next-line
  }, [page, perPage, search]);

  const handleViewArticle = (article) => {
    setSelectedArticle(article);
    setShowArticleModal(true);
  };

  const handleCloseArticleModal = () => {
    setSelectedArticle(null);
    setShowArticleModal(false);
  };

  const handlePageChange = page => setPage(page);
  
  const handlePerRowsChange = newPerPage => {
    setPerPage(newPerPage);
    setPage(1);
  };
  
  const handleSearch = e => {
    setSearch(e.target.value);
    setPage(1);
  };

  return (
    <>
      {error && <div className="alert alert-danger">{error}</div>}
      <input 
        type="text" 
        placeholder="Search..." 
        value={search} 
        onChange={handleSearch} 
        className="form-control mb-3" 
      />
      <DataTable
        columns={articleColumns}
        data={articles}
        progressPending={loading}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
        highlightOnHover
        striped
        dense
      />
      <ArticleModal 
        show={showArticleModal}
        article={selectedArticle}
        onClose={handleCloseArticleModal}
      />
    </>
  );
};

export default ArticleArchive;